import { useTranslations } from '@/i18n/use-locale';
import { useModerationDecisionMutation, type ModerationDecisionInput } from '@/lib/mutations';
import type { ModerationSubject } from '@/lib/query-keys';

/**
 * Approve, or reject with a reason, from any screen that shows a queued item.
 *
 * The same decision used to be written three times — the two queue lists and the review
 * screen — and behaved differently per copy (review RV-B10). One hook, so every screen
 * tells the admin the same things: the decision's own toast on success, the danger
 * toast on failure with the item left where it was, and the queue and the badges
 * refreshed (the mutation's own invalidations, in `lib/mutations.ts`).
 *
 * `onDecided` is what the calling screen does next — close its confirmation, leave for
 * the list — and only runs when the API said yes. `rejectedToast` words a rejection the
 * subject's default does not describe (a video introduction has no item to delete);
 * returning nothing keeps the default.
 */
export function useDecideModeration(
  subject: ModerationSubject,
  onDecided?: (input: ModerationDecisionInput) => void,
  rejectedToast?: (input: ModerationDecisionInput) => string | undefined,
) {
  const t = useTranslations(subject === 'organization' ? 'admin.verification' : 'admin.documents');

  return useModerationDecisionMutation(subject, {
    okToast: (_result, input) =>
      input.decision === 'approve'
        ? t('toast.approved')
        : (rejectedToast?.(input) ?? t('toast.rejected')),
    failToast: t('toast.failed'),
    onSuccess: (_result, input) => onDecided?.(input),
  });
}
